<script setup>
  import {computed, ref} from "vue";

  import ComponentDialog from "@components/Dialogs/Dialog.vue";
  import {router, useForm, usePage} from "@inertiajs/vue3";
  import additionalInformationModel from "@models/additionalInformationModel.js";
  import deviantMedicationModel from "@models/deviantMedicationModel.js";
  import MedicationComponentDetailDialogAdditionalData from "@pages/Records/Components/Sections/MedicationBar/Components/DetailDialog/AdditionalData.vue";
  import MedicationComponentDetailDialogDosageData from "@pages/Records/Components/Sections/MedicationBar/Components/DetailDialog/DosageData.vue";
  import MedicationComponentDetailDialogMedicationData from "@pages/Records/Components/Sections/MedicationBar/Components/DetailDialog/MedicationData.vue";
  import MedicationComponentDetailDialogNonComplianceData from "@pages/Records/Components/Sections/MedicationBar/Components/DetailDialog/NonComplianceData.vue";
  import {MedButton} from "@pharma4u/medicheck-components";
  import {cloneDeep} from "es-toolkit";
  import {get, isEmpty, set} from "es-toolkit/compat";

  defineOptions({
    name: "MedicationComponentDetailDialog",
  });

  defineProps({
    isEditable: {
      type: Boolean,
      required: true,
    },
  });

  const page = usePage();

  const refDetailDialog = ref(null);

  const isLoading = ref(false);
  const invalidFields = ref([]);

  const medicationForm = useForm({
    value: null,
  });

  const primaryButtonDisabled = computed(() => invalidFields.value.length > 0);

  const open = ({medication}) => {
    medicationForm.value = cloneDeep(medication);

    if (medicationForm.value.deviantmedication === null) {
      medicationForm.value.deviantmedication = cloneDeep(deviantMedicationModel);
    }

    if (!medicationForm.value.deviantmedication.dosage.selected_unit) {
      // get default unit from normal dosage
      medicationForm.value.deviantmedication.dosage.selected_unit = medicationForm.value.dosage.selected_unit;
      medicationForm.value.deviantmedication.dosage.selected_unit_key_doe =
        medicationForm.value.dosage.selected_unit_key_doe;
    }

    if (medicationForm.value.additionalinformation === null) {
      medicationForm.value.additionalinformation = cloneDeep(additionalInformationModel);
    }

    refDetailDialog.value.open();
  };

  const close = () => {
    if (refDetailDialog.value) {
      refDetailDialog.value.close();
    }
  };

  // The deviant unit is only prefilled as a default when the dialog opens (see
  // open()), so it goes stale as soon as the medication's own unit changes.
  const deviantUnitKeys = {
    "dosage.selected_unit": "deviantmedication.dosage.selected_unit",
    "dosage.selected_unit_key_doe": "deviantmedication.dosage.selected_unit_key_doe",
  };

  const changeForm = ({key, newValue}) => {
    const deviantKey = deviantUnitKeys[key];

    // Only carry the change over while the deviant side still holds the
    // prefilled default. A unit the user picked there is a real deviation and
    // must not be overwritten.
    if (deviantKey && get(medicationForm.value, deviantKey) === get(medicationForm.value, key)) {
      set(medicationForm.value, deviantKey, newValue);
    }

    set(medicationForm.value, key, newValue);
  };

  const addSymptom = () => {
    const medicationData = page.props.medications?.data.find((pageItem) => pageItem.id === medicationForm.value.id);

    if (
      medicationForm.value.additionalinformation?.satisfactionSymptom &&
      // proxy object is not empty {}
      Object.keys(medicationForm.value.additionalinformation?.satisfactionSymptom).length > 0 &&
      medicationForm.value.additionalinformation?.satisfactionSymptom?.Name !==
        medicationData.additionalinformation?.satisfactioncomment
    ) {
      router.post(
        route("symptoms.store", {
          patient: page.props.patient.id,
          record: page.props.record.id,
        }),
        medicationForm.value.additionalinformation.satisfactionSymptom,
        {
          preserveScroll: true,
          only: ["flash", "errors", "symptoms"],
        },
      );
    }
  };

  const patchMedication = () => {
    medicationForm
      .transform((data) => ({
        ...data.value,
      }))
      .put(
        route("medications.update", {
          patient: page.props.patient.id,
          record: page.props.record.id,
          medication: medicationForm.value.id,
        }),
        {
          preserveScroll: true,
          only: ["flash", "errors", "medications", "symptoms"],
          onStart: () => {
            isLoading.value = true;
          },
          onSuccess: (response) => {
            if (!isEmpty(response.props.errors)) {
              console.error(response.props.errors);
            }

            addSymptom();
            close();
          },
          onFinish: () => {
            isLoading.value = false;
          },
        },
      );
  };

  const handleValid = (errorId) => {
    invalidFields.value = invalidFields.value.filter((id) => {
      return id !== errorId;
    });
  };

  const handleInvalid = (errorId) => {
    if (!invalidFields.value.includes(errorId)) {
      invalidFields.value.push(errorId);
    }
  };

  const resetMedicationIndication = () => {
    const medication = cloneDeep(
      page.props.medications?.data.find((oldMedication) => oldMedication.id === medicationForm.value.id),
    );

    changeForm({key: "indications", newValue: medication.indications});
    changeForm({key: "indication_unknown", newValue: medication.indication_unknown});
  };

  defineExpose({
    open,
    close,
  });
</script>

<template>
  <ComponentDialog ref="refDetailDialog" dialog-width="large" :is-loading="isLoading" :test-id="getTestId('detail')">
    <template #content>
      <div class="flex flex-col space-y-3">
        <div class="sticky -top-5 z-20 bg-white py-3">
          <MedicationComponentDetailDialogMedicationData
            :is-editable="isEditable"
            :medication-form="medicationForm"
            :test-id="getTestId('detail-dialog')"
            @change-form="changeForm"
            @valid="handleValid"
            @invalid="handleInvalid"
            @reset-indications="resetMedicationIndication"
          />
        </div>

        <MedicationComponentDetailDialogDosageData
          :is-editable="isEditable"
          :medication-form="medicationForm"
          :test-id="getTestId('detail-dialog')"
          @change-form="changeForm"
          @valid="handleValid"
          @invalid="handleInvalid"
        />

        <MedicationComponentDetailDialogAdditionalData
          :is-editable="isEditable"
          :medication-form="medicationForm"
          :test-id="getTestId('detail-dialog')"
          @change-form="changeForm"
          @valid="handleValid"
          @invalid="handleInvalid"
        />

        <MedicationComponentDetailDialogNonComplianceData
          :is-editable="isEditable"
          :medication-form="medicationForm"
          :test-id="getTestId('detail-dialog')"
          @change-form="changeForm"
          @valid="handleValid"
          @invalid="handleInvalid"
        />
      </div>
    </template>

    <template #actions>
      <template v-if="isEditable">
        <MedButton
          variant="primary"
          :disabled="primaryButtonDisabled"
          :data-test="getTestId('detail-dialog-save-button')"
          @click="patchMedication"
        >
          Speichern
        </MedButton>

        <MedButton :disabled="isLoading" :data-test="getTestId('detail-dialog-abort-button')" @click="close">
          Abbrechen
        </MedButton>
      </template>

      <template v-else>
        <MedButton :disabled="isLoading" :data-test="getTestId('detail-dialog-close-button')" @click="close">
          Schließen
        </MedButton>
      </template>
    </template>
  </ComponentDialog>
</template>
